'use client'

import { Button } from '@/core/components/form/button/button'
import { useState } from 'react'
import { LuCamera, LuX } from 'react-icons/lu'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import CommentInput from '@/features/veluvit/components/comments/comment-input'

import type { WorkComments } from '@/features/veluvit/components/comments/use-work-comments'
import type { Snap } from '@/features/veluvit/types/api.types'

/** 스냅 캡션 한도 — 댓글 첫머리를 캡션으로 쓴다 */
const CAPTION_MAX = 200

/**
 * 새 댓글 쓰기(원댓글) — 한 줄 입력(누르면 취소 · 등록). 답글은 각 댓글 아래에서 따로 연다(comment-list).
 * snapRoom 을 주면(상세 화면) 이 작품의 내 대화방에서 남긴 스냅을 붙일 수 있다 — 스냅 댓글은 검수 뒤 공개.
 */
export default function CommentComposer({
  c,
  snapRoom,
  className,
}: {
  c: WorkComments
  snapRoom?: { id: string; snaps: Snap[] }
  className?: string
}) {
  const [snapId, setSnapId] = useState<string | null>(null)
  const [picking, setPicking] = useState(false)
  const snaps = snapRoom?.snaps || []
  const picked = snaps.find((s) => s.id === snapId)

  const send = async (text: string) => {
    const ok = await c.post(text, {
      snap:
        picked && snapRoom
          ? {
              roomId: snapRoom.id,
              snapId: picked.id,
              caption: text.trim().slice(0, CAPTION_MAX),
            }
          : undefined,
    })
    if (ok) {
      setSnapId(null)
      setPicking(false)
    }
    return ok
  }

  return (
    <CommentInput
      className={className}
      placeholder="댓글 추가…"
      submitLabel="등록"
      onSubmit={send}
      onCancel={() => {
        setSnapId(null)
        setPicking(false)
      }}
      extra={
        snapRoom ? (
          <Button
            type="button"
            aria-pressed={picking}
            onClick={() => setPicking((v) => !v)}
            className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-2.5 text-c13 text-t2 transition-colors hover:bg-foreground/[0.06] hover:text-t1 focus-visible:bg-foreground/[0.06] aria-pressed:text-point"
            variant="bare"
          >
            <LuCamera aria-hidden="true" className="size-4" />
            스냅 붙이기
          </Button>
        ) : undefined
      }
      below={
        snapRoom && (picked || picking) ? (
          <>
            {picked && (
              <div className="relative mt-2 aspect-[3/4] w-24 overflow-hidden rounded-field">
                <SafeImage
                  src={picked.image}
                  alt="붙인 스냅"
                  fill
                  sizes="96px"
                  unoptimized
                  className="object-cover"
                  appearance="plain"
                />
                <Button
                  type="button"
                  aria-label="스냅 빼기"
                  onClick={() => setSnapId(null)}
                  className="absolute top-1 right-1 grid size-6 cursor-pointer place-items-center rounded-full bg-black/45 text-white backdrop-blur-md"
                  variant="bare"
                >
                  <LuX aria-hidden="true" className="size-3.5" />
                </Button>
              </div>
            )}
            {picking && (
              <div className="mt-2 grid grid-cols-4 gap-1.5">
                {snaps.length ? (
                  snaps.map((s) => (
                    <Button
                      key={s.id}
                      type="button"
                      aria-pressed={snapId === s.id}
                      onClick={() => setSnapId(s.id)}
                      className={cn(
                        'relative aspect-[3/4] cursor-pointer overflow-hidden rounded-field',
                        snapId === s.id
                          ? 'shadow-[0_0_0_2px_var(--color-point)]'
                          : 'opacity-70 transition-opacity hover:opacity-100'
                      )}
                      variant="bare"
                    >
                      <SafeImage
                        src={s.image}
                        alt=""
                        fill
                        sizes="90px"
                        unoptimized
                        className="object-cover"
                        appearance="plain"
                      />
                    </Button>
                  ))
                ) : (
                  <p className="col-span-full text-c12 text-t3">
                    이 작품 대화에서 남긴 스냅이 아직 없어요.
                  </p>
                )}
              </div>
            )}
            {picked && (
              <p className="mt-2 text-c11h text-t3">
                스냅은 이미지와 댓글 첫 200자만 공개되고, 검수를 마친 뒤 보여요.
              </p>
            )}
          </>
        ) : undefined
      }
    />
  )
}
